import React, { useState, useEffect } from "react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  inputCls,
  MEETING_STATUSES,
  QUARTERS,
} from "./shared";
import Field from "./Field";
import DocumentList from "./DocumentList";

const emptyMeeting = (boardType) => ({
  board_type: boardType,
  financial_year_id: "",
  quarter: boardType === "executive" ? 1 : null,
  meeting_date: "",
  meeting_time: "",
  venue: "",
  status: "Scheduled",
  agenda: "",
  resolution: "",
  notes: "",
  documents: [],
  attendance: [],
});

export default function MeetingDialog({
  open,
  onClose,
  boardType,
  editing,
  financialYears,
  onSaved,
  writable,
}) {
  const [form, setForm] = useState(emptyMeeting(boardType));
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open) return;
    if (editing) {
      setForm({
        ...emptyMeeting(boardType),
        ...editing,
        meeting_date: editing.meeting_date || "",
        meeting_time: editing.meeting_time || "",
        documents: (editing.documents || []).map((d) => ({ ...d })),
      });
    } else {
      const currentFY = financialYears.find((f) => f.is_current);
      setForm({
        ...emptyMeeting(boardType),
        financial_year_id: currentFY?.id || financialYears[0]?.id || "",
      });
    }
  }, [open, editing, boardType, financialYears]);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const save = async () => {
    if (!form.financial_year_id) {
      toast.error("Pick a Financial Year");
      return;
    }
    if (!form.meeting_date) {
      toast.error("Meeting date is required");
      return;
    }
    if (boardType === "executive" && !form.quarter) {
      toast.error("Quarter is required for Executive Board meetings");
      return;
    }
    setSaving(true);
    try {
      const payload = {
        ...form,
        quarter: boardType === "executive" ? Number(form.quarter) : null,
      };
      let result;
      if (editing) {
        const { data } = await api.put(`/meetings/${editing.id}`, payload);
        result = data;
        toast.success("Meeting updated");
      } else {
        const { data } = await api.post(`/meetings`, payload);
        result = data;
        toast.success("Meeting created");
      }
      onSaved(result);
      onClose();
    } catch (err) {
      toast.error(formatApiErrorDetail(err?.response?.data?.detail) || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent
        className="bg-white border-[#e5e0d8] rounded-sm max-w-4xl max-h-[90vh] overflow-y-auto"
        data-testid="meeting-dialog"
      >
        <DialogHeader>
          <DialogTitle className="font-serif text-2xl text-[#1c1a17]">
            {editing ? "Edit Meeting" : "Add Meeting"} — {boardType === "executive" ? "Executive Board" : "General Board"}
          </DialogTitle>
        </DialogHeader>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-5 py-2">
          <Field label="Financial Year *">
            <select
              data-testid="meeting-fy-select"
              className={inputCls}
              value={form.financial_year_id}
              disabled={!writable}
              onChange={(e) => set("financial_year_id", e.target.value)}
            >
              <option value="">— Select —</option>
              {financialYears.map((fy) => (
                <option key={fy.id} value={fy.id}>
                  {fy.label}
                  {fy.is_current ? " (current)" : ""}
                </option>
              ))}
            </select>
          </Field>
          {boardType === "executive" && (
            <Field label="Quarter *">
              <select
                data-testid="meeting-quarter-select"
                className={inputCls}
                value={form.quarter || ""}
                disabled={!writable}
                onChange={(e) => set("quarter", Number(e.target.value))}
              >
                <option value="">— Select —</option>
                {QUARTERS.map((q) => (
                  <option key={q.value} value={q.value}>
                    {q.label}
                  </option>
                ))}
              </select>
            </Field>
          )}
          <Field label="Meeting Date *">
            <input
              data-testid="meeting-date-input"
              type="date"
              className={inputCls}
              value={form.meeting_date}
              disabled={!writable}
              onChange={(e) => set("meeting_date", e.target.value)}
            />
          </Field>
          <Field label="Meeting Time">
            <input
              data-testid="meeting-time-input"
              type="time"
              className={inputCls}
              value={form.meeting_time}
              disabled={!writable}
              onChange={(e) => set("meeting_time", e.target.value)}
            />
          </Field>
          <Field label="Venue / Location" full>
            <input
              data-testid="meeting-venue-input"
              className={inputCls}
              value={form.venue}
              disabled={!writable}
              placeholder="e.g., Trust HQ, Conference Hall"
              onChange={(e) => set("venue", e.target.value)}
            />
          </Field>
          <Field label="Status">
            <select
              data-testid="meeting-status-select"
              className={inputCls}
              value={form.status}
              disabled={!writable}
              onChange={(e) => set("status", e.target.value)}
            >
              {MEETING_STATUSES.map((s) => (
                <option key={s} value={s}>
                  {s}
                </option>
              ))}
            </select>
          </Field>
          <div />
          <Field label="Agenda" full>
            <textarea
              data-testid="meeting-agenda-input"
              className={inputCls}
              rows={4}
              value={form.agenda}
              disabled={!writable}
              placeholder="Outline the topics for discussion…"
              onChange={(e) => set("agenda", e.target.value)}
            />
          </Field>
          <Field label="Resolution Details" full>
            <textarea
              data-testid="meeting-resolution-input"
              className={inputCls}
              rows={4}
              value={form.resolution}
              disabled={!writable}
              placeholder="Resolutions passed during the meeting…"
              onChange={(e) => set("resolution", e.target.value)}
            />
          </Field>
          <Field label="Notes" full>
            <textarea
              className={inputCls}
              rows={2}
              value={form.notes}
              disabled={!writable}
              onChange={(e) => set("notes", e.target.value)}
            />
          </Field>
          <div className="md:col-span-2">
            <div className="text-[10px] tracking-[0.2em] uppercase text-[#6b655b] font-semibold mb-3">
              Meeting Documents (Notice, Agenda, MoM, Resolution, Supporting)
            </div>
            <DocumentList
              docs={form.documents}
              onChange={(docs) => set("documents", docs)}
              writable={writable}
            />
          </div>
        </div>
        <DialogFooter className="pt-4">
          <button
            onClick={onClose}
            className="bg-white hover:bg-[#f9f8f6] text-[#1c1a17] border border-[#e5e0d8] text-sm px-5 py-2 rounded-sm"
          >
            Cancel
          </button>
          {writable && (
            <button
              onClick={save}
              disabled={saving}
              data-testid="meeting-save-button"
              className="bg-[#234333] hover:bg-[#1b3326] text-white text-sm px-5 py-2 rounded-sm disabled:opacity-60"
            >
              {saving ? "Saving…" : editing ? "Update" : "Create"}
            </button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
